import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLibraryContentDto } from '@asset-metrix/portal-gateway-client-js';
import { Button, Modal } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';

import {
  type ContentLibraryContentsQueryKey,
  fetchContentLibraryCategories,
  fetchContentLibraryContents,
} from '../../api/portalGateway/contentLibrary';
import { prependRoleId } from '../../utils/prependRoleId';

import {
  categoriesWithVisibleItems,
  resolveCategorySelections,
} from './contentLibraryCategoryPaths';
import { groupContentsForDisplay } from './groupContentsForDisplay';
import { SelectComponentsStep } from './SelectComponentsStep';
import { SelectDataSourceStep } from './SelectDataSourceStep';

import styles from './addComponentToDashboardModal.module.scss';

export type AddComponentSelection = ContentLibraryContentDto;

export type AddComponentToDashboardModalProps = {
  onAddComponents: (contents: AddComponentSelection[]) => void;
  onClose: () => void;
  opened: boolean;
  withinPortal?: boolean;
};

export function AddComponentToDashboardModal({
  onAddComponents,
  onClose,
  opened,
  withinPortal,
}: AddComponentToDashboardModalProps) {
  const { t } = useTranslation();
  const untitledLabel = t('app.page.editAction.addComponentModal.untitled');
  const [categorySelectionOverrides, setCategorySelectionOverrides] = useState<
    Record<string, string>
  >({});
  const [selectedContentIndexes, setSelectedContentIndexes] = useState<
    Set<number>
  >(() => new Set());

  const categoriesQuery = useQuery({
    enabled: opened,
    queryFn: fetchContentLibraryCategories,
    queryKey: prependRoleId(['contentLibraryCategories']),
  });

  const categories = categoriesQuery.data?.categories ?? [];
  const validPaths = categoriesQuery.data?.validPaths ?? [];
  const categorySelections = resolveCategorySelections(
    categories,
    validPaths,
    categorySelectionOverrides
  );
  const visibleCategories = categoriesWithVisibleItems(
    categories,
    validPaths,
    categorySelections
  );
  const selectedTags = categories.map(
    (category) => categorySelections[category.id] ?? ''
  );
  const selectedTagsKey = selectedTags.join('\u001F');
  const canSearchContents =
    opened &&
    categoriesQuery.isSuccess &&
    categories.length > 0 &&
    selectedTags.length > 0 &&
    selectedTags.every(Boolean);

  const contentsQuery = useQuery({
    enabled: canSearchContents,
    queryFn: fetchContentLibraryContents,
    queryKey: prependRoleId<ContentLibraryContentsQueryKey>([
      'contentLibraryContents',
      selectedTags,
    ]),
  });

  const displayContents = useMemo(
    () => groupContentsForDisplay(contentsQuery.data ?? [], untitledLabel),
    [contentsQuery.data, untitledLabel]
  );

  useEffect(() => {
    setSelectedContentIndexes(new Set());
  }, [selectedTagsKey]);

  const resetState = useCallback(() => {
    setCategorySelectionOverrides({});
    setSelectedContentIndexes(new Set());
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleAdd = useCallback(() => {
    const selected = displayContents.filter((_, index) =>
      selectedContentIndexes.has(index)
    );
    onAddComponents(selected);
    handleClose();
  }, [displayContents, handleClose, onAddComponents, selectedContentIndexes]);

  const toggleIndex = useCallback((index: number, checked: boolean) => {
    setSelectedContentIndexes((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  }, []);

  const tables = displayContents.filter(
    (item) => item.content.entity.type === 'table'
  );
  const charts = displayContents.filter(
    (item) => item.content.entity.type === 'vegaChart'
  );

  const actions = (
    <>
      <Button onClick={handleClose} variant="secondary">
        {t('app.page.editAction.addComponentModal.cancel')}
      </Button>
      {categories.length > 0 && (
        <Button
          disabled={selectedContentIndexes.size === 0}
          onClick={handleAdd}
          variant="primary"
        >
          {t('app.page.editAction.addComponentModal.add')}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      actions={actions}
      modalType="action"
      onClose={handleClose}
      opened={opened}
      size={900}
      title={t('app.page.editAction.addComponentModal.title')}
      withinPortal={withinPortal}
    >
      <div className={styles['body']}>
        <SelectDataSourceStep
          categories={visibleCategories}
          categorySelections={categorySelections}
          isError={categoriesQuery.isError}
          isPending={categoriesQuery.isPending}
          isSuccess={categoriesQuery.isSuccess}
          onCategoryChange={(categoryId, tagName) => {
            setCategorySelectionOverrides((current) => {
              const next = { ...current };
              if (tagName == null) {
                delete next[categoryId];
              } else {
                next[categoryId] = tagName;
              }
              return next;
            });
          }}
        />
        {canSearchContents && (
          <div className={styles['results']}>
            <SelectComponentsStep
              charts={charts}
              displayContents={displayContents}
              isError={contentsQuery.isError}
              isPending={contentsQuery.isPending}
              isSuccess={contentsQuery.isSuccess}
              onToggleIndex={toggleIndex}
              selectedContentIndexes={selectedContentIndexes}
              tables={tables}
              untitledLabel={untitledLabel}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
